import { Trans, useTranslation } from 'react-i18next';
import { TableDataHeader } from '@block-explorer/ui';
import { TableRow, TableRowType } from '@make-software/cspr-design';

import {
  HashFilterWithCsprName,
  PageDataTable,
  TabDataTable,
} from '../../../components';
import AccountTransferListItem from './AccountTransferListItem';
import { useGetPaginatedAccountTransfers } from '../../../hooks/event-store-hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import React from 'react';

export interface AccountTransferListProps {
  accountHash: string;
  pageSize?: number;
  showTopPagination?: boolean;
}

export const AccountTransferList = ({
  accountHash,
  pageSize = 10,
  showTopPagination,
}: AccountTransferListProps) => {
  const { t } = useTranslation();

  const { selectedCurrency: currency } = useSelector(
    (state: RootState) => state.currencies
  );

  const { data, loading, error, paginationProps, sortingProps, filterProps } =
    useGetPaginatedAccountTransfers(
      accountHash
        ? {
            hash: accountHash,
            includes: `initiator_public_key,to_public_key,from_purse_public_key,to_purse_public_key,to_account_info,to_centralized_account_info,from_purse_centralized_account_info,to_purse_centralized_account_info,from_purse_account_info,to_purse_account_info,rate(${currency.id}),from_purse_cspr_name,to_purse_cspr_name`,
            defaultPageSize: pageSize,
          }
        : null
    );
  const TableComponent = showTopPagination ? PageDataTable : TabDataTable;
  const hasNothingFound = !data?.length && !loading;
  return (
    <TableComponent
      loading={!data || loading}
      error={error}
      noDataError={hasNothingFound}
      tableRowType={TableRowType.TextWithAvatar}
      data={data}
      sortingProps={sortingProps}
      paginationProps={paginationProps}
      renderDataHeader={({ sortingProps }) => (
        <TableRow>
          <TableDataHeader fixedWidthRem={12.625}>
            {t('Transaction Hash')}
          </TableDataHeader>
          <TableDataHeader
            reverseSortingDirection
            sortableColumnField={'timestamp'}
            {...sortingProps}
            fixedWidthRem={8.625}
          >
            <Trans t={t}>Age</Trans>
          </TableDataHeader>
          <TableDataHeader
            {...sortingProps}
            fixedWidthRem={15.75}
            filter={
              <HashFilterWithCsprName
                {...filterProps}
                filterId={'from_hash'}
                purseFilterId="from_purse"
              />
            }
          >
            <Trans t={t}>From</Trans>
          </TableDataHeader>
          <TableDataHeader
            {...sortingProps}
            fixedWidthRem={15.75}
            filter={
              <HashFilterWithCsprName
                {...filterProps}
                filterId={'to_hash'}
                purseFilterId="to_purse"
              />
            }
          >
            <Trans t={t}>To</Trans>
          </TableDataHeader>
          <TableDataHeader {...sortingProps} fixedWidthRem={7.5}>
            <Trans t={t}>Transfer ID</Trans>
          </TableDataHeader>
          <TableDataHeader align="right">
            <Trans t={t}>Amount</Trans>
          </TableDataHeader>
        </TableRow>
      )}
      renderPaginatedData={(transfers) =>
        transfers.map((transfer, idx) => {
          return (
            <AccountTransferListItem
              key={`${idx}-${transfer.deploy_hash}`}
              loading={!data}
              data={transfer}
            />
          );
        })
      }
    />
  );
};

export default AccountTransferList;
